import React, { useContext, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ContentFilterSubFilterWrapper } from '../ContentFilter';
import { ImportSourceArgsGroupInput, PlanImporterType } from '../../../../models/graphql';
import { SourceLevelAction } from '../SourceLevelAction';
import { FindContentContext } from '../FindContentProvider';
import classNames from 'classnames';
import classes from '../ContentFilter/ContentFilter.module.scss';
import { Search } from '../../../../components/forms/Search';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { Input } from '../../../../components/forms/Input';
import { useDimensions } from '../../../../hooks/useDimensions';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { AdvancedFeature } from 'components/containers/AdvancedFeature';
import { AdvancedFeatures } from 'components/containers/AdvancedFeature/AdvancedFeature.view';

export type RssFilterBarProps = {
  formInstance: UseFormReturn;
  submitFormHandler: (clearFilterBar?: boolean) => void;
  width: number;
};

export type ContentFilterRssViewProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  submitFormHandler: (clearFilterBar?: boolean) => void;
  hasPosts: boolean;
  showSaveSourceModal: boolean;
  setShowSaveSourceModal: (show: boolean) => void;
  getRssInputArgs: () => ImportSourceArgsGroupInput;
  apolloErrorKey: string | undefined;
};

export const RssFilterBar = (props: RssFilterBarProps): JSX.Element => {
  const { formInstance, submitFormHandler, width } = props;
  const { loading } = useContext(FindContentContext);
  const { importType, isTrial } = useOrganizationPlan();
  const isDisabledFeature = importType !== PlanImporterType.ADVANCED_IMPORTER;
  const filterBarContainerClasses = classNames('grid-x', classes.FilterBarContainer);

  const filterInputWrapperClasses = classNames('cell', 'auto', classes.InputWrapper, {
    [classes.InputWrapperLargeFilterWidth]: width >= BreakpointsValuesEnum.large
  });
  const filterButtonWrapperClasses = classNames('cell', classes.FilterButtonContainer, {
    shrink: width < BreakpointsValuesEnum.large
  });

  return (
    <div className={filterBarContainerClasses}>
      <div className={filterInputWrapperClasses}>
        <Input
          formInstance={formInstance}
          name={'term'}
          placeholder={'Search within these posts'}
          rules={{}}
          size={'medium'}
          disabled={loading || isDisabledFeature}
          onKeyDown={e => {
            // ENTER key presses submits form
            if (e.key === 'Enter') {
              props.submitFormHandler();
            }
          }}
        />
      </div>
      <div className={filterButtonWrapperClasses}>
        <Button
          type={'button'}
          variant={'hollow'}
          size={'medium'}
          color={ButtonColorsEnum.primary}
          disabled={loading || isDisabledFeature}
          expanded={true}
          action={() => submitFormHandler()}>
          Apply
        </Button>
      </div>
      {(isTrial || isDisabledFeature) && (
        <div className={classNames('cell', classes.AdvancedFeatureContainer)}>
          <AdvancedFeature feature={AdvancedFeatures.filterFeed} />
        </div>
      )}
    </div>
  );
};

export const ContentFilterRssView = (props: ContentFilterRssViewProps): JSX.Element => {
  const {
    formInstance,
    showFilters,
    setShowFilters,
    showSaveSourceModal,
    setShowSaveSourceModal,
    getRssInputArgs,
    submitFormHandler,
    apolloErrorKey
  } = props;
  const { loading, error } = useContext(FindContentContext);
  const bodyRef = useRef<HTMLElement>(document.body);
  const { width } = useDimensions(bodyRef);

  // When the form changes, we do not show sub-filters & we reset the type
  useEffect(() => {
    return () => {
      setShowFilters(false);
      formInstance.resetField('type');
    };
  }, [formInstance, setShowFilters]);

  const searchWrapperClasses = classNames('cell', classes.InputWrapper, classes.InputWrapperIgnoreMargin, {
    'small-6': width < BreakpointsValuesEnum.large,
    auto: width >= BreakpointsValuesEnum.large,
    [classes.InputWrapperRssFilterWidth]: width >= BreakpointsValuesEnum.large
  });

  return (
    <>
      <div className={searchWrapperClasses}>
        <Search
          name={'url'}
          placeholder={'Type a valid RSS feed URL'}
          label={'Search for blog posts'}
          action={() => {
            submitFormHandler();
          }}
          formInstance={formInstance}
          rules={{}}
          disabled={loading}
        />
      </div>
      <ContentFilterSubFilterWrapper showFilters={showFilters}>
        <RssFilterBar formInstance={formInstance} submitFormHandler={submitFormHandler} width={width} />
      </ContentFilterSubFilterWrapper>
      {showFilters && (
        <SourceLevelAction
          formInstance={formInstance}
          setShowSaveSourceModal={setShowSaveSourceModal}
          showSaveSourceModal={showSaveSourceModal}
          getArgsHandler={getRssInputArgs}
        />
      )}
      {error && (
        <div className={'cell'}>
          <div className={classes.ErrorNotificationBox}>
            <div>{error}</div>
            {apolloErrorKey && apolloErrorKey !== 'EXTERNAL_RESOURCE_INVALID' && (
              <div className={classes.ActionButtonContainer}>
                <Button
                  type={'button'}
                  variant={'hollow'}
                  size={'small'}
                  color={ButtonColorsEnum.alert}
                  action={() => props.submitFormHandler()}>
                  <span>Try Again</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
